<template>
    <Modal ref="modal"
           title="שליחת קישור בוואטסאפ"
           :hideFooter="true">

      <template v-slot:body>
        <div style="padding: 20px;">
          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px; font-weight: 600;">מספר טלפון</label>
            <div style="position: relative;">
              <input v-model="whatsappPhoneNumber"
                     type="text"
                     class="form-control"
                     placeholder="הקלידו מספר או שם לחיפוש לקוח"
                     autocomplete="off"
                     @blur="closeSuggestionsSoon"
                     style="text-align: right;">
              <div v-if="suggestions.length" class="phoneSuggestions">
                <!-- how many were found: the list scrolls, every match is in it -->
                <div class="phoneSuggestionsLine">{{ suggestionsLine }}</div>
                <div v-for="suggestion in suggestions"
                     :key="suggestion.key"
                     class="phoneSuggestion"
                     @mousedown.prevent="pickSuggestion(suggestion)">
                  <img style="height:30px;width:30px;border-radius:100%;margin-left:10px;flex:none"
                       loading="lazy"
                       :src="suggestion.pic ? 'https://planup-il.s3.eu-central-1.amazonaws.com/' + suggestion.pic : $globals.getLetterImage(suggestion.name)">
                  <span class="suggestionName">{{ suggestion.name }}</span>
                  <span class="suggestionPhone">{{ suggestion.phone }}</span>
                </div>
              </div>
            </div>
            <div v-if="bookingInviteName" class="inviteHint">
              <i class="fa fa-user-check"></i>
              הקישור מותאם אישית — הפרטים של {{ bookingInviteName }} ימולאו מראש בטופס
            </div>
          </div>

          <!-- Which calendar the link books on: the main one, or a crew member's own separate
               calendar (only when at least one crew member has one switched on) -->
          <div v-if="bookingInvite && crewCalendars.length" style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px; font-weight: 600;">יומן לקביעת הפגישה</label>
            <select class="form-control bookingCalendarSelect"
                    :value="bookingCrewMemberId || ''"
                    @change="selectBookingCalendar($event.target.value)">
              <option value="">היומן הראשי</option>
              <option v-for="crew in crewCalendars" :key="crew.id" :value="crew.id">{{ crew.name }}</option>
            </select>
            <div v-if="bookingCrewMemberId" class="inviteHint" style="color:#5b4a86">
              <i class="fa fa-user"></i>
              הלקוח יבחר מועד מתוך הזמנים הפנויים של {{ selectedCrewName }}, והפגישה תשויך אליו
            </div>
          </div>

          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px; font-weight: 600;">הודעה</label>
            <textarea v-model="whatsappMessage"
                      class="form-control"
                      rows="4"
                      style="text-align: right; resize: vertical;"></textarea>
            <!-- where the invitation's standing wording is set (BookingInvite.js) -->
            <div v-if="bookingInvite && canEditInviteWording" class="inviteWordingHint">
              הנוסח הקבוע של ההודעה נקבע ב<span class="inviteWordingLink" @click="openBookingSettings">הגדרות ← תיאום פגישות אונליין</span>
            </div>
          </div>

          <div style="display: flex; justify-content: space-between; gap: 10px;">
            <button class="btn btn-outline-primary"
                    @click="copyLink"
                    style="flex: 1;">
              העתק קישור
            </button>
            <button class="btn btn-primary"
                    @click="sendLinkViaWhatsapp"
                    :disabled="!whatsappPhoneNumber || !whatsappMessage"
                    style="flex: 1;">
              שלח
            </button>
          </div>
        </div>
      </template>
    </Modal>
</template>

<script>
import axiosV2 from '../../core/http/axios_api_v2';
import { debounce } from '../../helpers/interaction';
import Config from '../../Config';
import hasPermission from '../../core/acl/acl';
import { contactSuggestions, contactSuggestionsLine } from '../../helpers/contact-search';

export default {
    name: 'SendLinkViaWhatsappModal',
    data() {
        return {
            whatsappPhoneNumber: '',
            whatsappMessage: '',
            suggestions: [],
            // the api had more rows than it sends (search-results findAll's limit)
            suggestionsMore: false,
            // Set when the value is filled programmatically (picking a suggestion / show() prefill)
            // so the watcher doesn't reopen the dropdown for it.
            suppressSearch: false,
            searchRandom: null,
            // Booking-link personalisation: when the message carries the public booking link and
            // the chosen phone belongs to a contact of ours, the link is swapped for one that
            // opens the form pre-filled with their details.
            bookingInvite: false,
            bookingBaseLink: '',
            bookingCurrentLink: '',
            bookingInviteName: '',
            // the invite code of the chosen phone's contact ('' = none) and the crew member whose
            // separate calendar the link points at (null = the main calendar) — the link in the
            // message is recomposed from these two whenever either changes
            bookingInviteCode: '',
            bookingCrewMemberId: null,
            crewCalendars: [],
            inviteTimer: null,
            inviteRandom: null,
        }
    },

    computed: {
        selectedCrewName() {
            const crew = this.crewCalendars.find(c => String(c.id) === String(this.bookingCrewMemberId));
            return crew ? crew.name : '';
        },
        // the booking settings page asks for settings.view
        canEditInviteWording() {
            return hasPermission(this.$store.getters.getUser, 'settings.view');
        },
        suggestionsLine() {
            return contactSuggestionsLine(this.suggestions.length, this.suggestionsMore);
        },
    },

    watch: {
        whatsappPhoneNumber() {
            // Runs for typed AND programmatic values (picking a suggestion is how most sends
            // choose a customer), so it can't sit behind the suppressSearch early-return below.
            if (this.bookingInvite) {
                clearTimeout(this.inviteTimer);
                this.inviteTimer = setTimeout(this.refreshBookingInvite, 500);
            }
            if (this.suppressSearch) {
                this.suppressSearch = false;
                return;
            }
            if (!this.whatsappPhoneNumber || this.whatsappPhoneNumber.trim().length < 2) {
                this.suggestions = [];
                return;
            }
            debounce(this.searchCustomers, 400)();
        }
    },

    methods: {
        // options.bookingBaseLink — the booking link inside `message`. Passing it turns on
        // personalisation: the link is rewritten to an invite link once a known phone is chosen,
        // and a calendar picker appears when crew members have separate calendars.
        // options.crewMemberId — the crew calendar the passed link already points at, if any.
        show(message, phone, options) {
            options = options || {};
            this.whatsappMessage = message || '';
            this.suppressSearch = !!phone;
            this.whatsappPhoneNumber = phone || '';
            this.suggestions = [];
            this.bookingBaseLink = options.bookingBaseLink || '';
            this.bookingCurrentLink = this.bookingBaseLink;
            this.bookingInvite = !!this.bookingBaseLink;
            this.bookingInviteName = '';
            this.bookingInviteCode = '';
            this.bookingCrewMemberId = options.crewMemberId || null;
            this.crewCalendars = [];
            if (this.bookingInvite) this.loadCrewCalendars();
            this.$refs.modal.show();
        },

        // The crew members with a separate booking calendar switched on (empty = no picker).
        loadCrewCalendars() {
            axiosV2('/booking/crew-calendars').then(response => {
                this.crewCalendars = Array.isArray(response.data) ? response.data : [];
                // a preselected member whose calendar is off falls back to the main calendar
                if (this.bookingCrewMemberId && !this.crewCalendars.some(c => String(c.id) === String(this.bookingCrewMemberId))) {
                    this.bookingCrewMemberId = null;
                    this.recomposeBookingLink();
                }
            }).catch(() => { });
        },

        selectBookingCalendar(value) {
            this.bookingCrewMemberId = value ? +value : null;
            this.recomposeBookingLink();
        },

        // The link in the message = tenant + chosen calendar + the contact's invite code.
        recomposeBookingLink() {
            this.applyBookingLink(
                Config.bookingLink(this.$store.state.tenantId, this.bookingInviteCode, this.bookingCrewMemberId),
                this.bookingInviteName);
        },

        openBookingSettings() {
            this.hide();
            if (this.$route.path !== '/settings/booking') this.$router.push('/settings/booking');
        },

        hide() {
            this.$refs.modal.hide();
            this.whatsappPhoneNumber = '';
            this.whatsappMessage = '';
            this.suggestions = [];
            this.bookingInvite = false;
            this.bookingInviteName = '';
            this.bookingInviteCode = '';
            this.bookingCrewMemberId = null;
            this.crewCalendars = [];
            clearTimeout(this.inviteTimer);
        },

        // Ask the server whether this phone is a contact of ours; if it is, swap the plain
        // booking link in the message for their personalised one (and back out when it isn't).
        refreshBookingInvite() {
            if (!this.bookingInvite) return;
            const phone = (this.whatsappPhoneNumber || '').replace(/[\s\-+()]/g, '');
            if (!/^\d{7,15}$/.test(phone)) {
                this.bookingInviteCode = '';
                this.bookingInviteName = '';
                this.recomposeBookingLink();
                return;
            }
            this.inviteRandom = Math.random();
            const requestRandom = this.inviteRandom;
            axiosV2(`/booking/invite-code?phone=${encodeURIComponent(phone)}`).then(response => {
                if (requestRandom !== this.inviteRandom) return;
                const data = response.data || {};
                this.bookingInviteCode = data.code || '';
                this.bookingInviteName = data.code ? (data.name || '') : '';
                this.recomposeBookingLink();
            }).catch(() => { });
        },

        applyBookingLink(link, name) {
            this.bookingInviteName = name || '';
            if (!link || link === this.bookingCurrentLink) return;
            if (this.bookingCurrentLink) {
                this.whatsappMessage = this.whatsappMessage.split(this.bookingCurrentLink).join(link);
            }
            this.bookingCurrentLink = link;
        },

        // Search customers by what was typed — phone digits or a name. search_result.searchText
        // contains both, so one endpoint covers both cases.
        searchCustomers() {
            let term = (this.whatsappPhoneNumber || '').trim();
            // "052-247 7699" → "0522477699" so phone fragments match the digits in searchText
            if (/^[\d\s\-+]+$/.test(term)) term = term.replace(/[\s\-+]/g, '');
            if (term.length < 2) return;

            this.searchRandom = Math.random();
            const requestRandom = this.searchRandom;
            axiosV2(`/search-results/?term=${encodeURIComponent(term)}&random=${requestRandom}`).then(response => {
                if (!response.data || response.data.random != this.searchRandom) return;
                // one row per contact-with-phone, deduped by name + number, every one of them
                // (it stopped at 7) - the ones whose name / phone matched first (contact-search.js)
                this.suggestions = contactSuggestions(response.data.results || [], response.data.words || term.split(' '));
                this.suggestionsMore = !!response.data.more;
            }).catch(() => { });
        },

        pickSuggestion(suggestion) {
            this.suppressSearch = true;
            this.whatsappPhoneNumber = suggestion.phone;
            this.suggestions = [];
        },

        // On blur, wait a beat so a mousedown on a suggestion lands first.
        closeSuggestionsSoon() {
            setTimeout(() => { this.suggestions = []; }, 200);
        },

        sendLinkViaWhatsapp() {
            if (!this.whatsappPhoneNumber || !this.whatsappMessage) {
                return;
            }

            // Use the same sendMessage method as in PlanupMultiMessagesChat
            this.$handlers.whatsappHandler.sendMessage(this.whatsappPhoneNumber, this.whatsappMessage);


            // Close the modal and reset fields
            this.hide();
        },

        // A booking invitation copies its link alone, ready for an Instagram bio or a website:
        // the business's plain link, or the personal one once a customer's phone was chosen.
        // Other links still copy the whole message.
        copyLink() {
            const text = this.bookingInvite && this.bookingCurrentLink ? this.bookingCurrentLink : this.whatsappMessage;
            navigator.clipboard.writeText(text).then(() => {
                this.$notify({
                    type: 'success',
                    title: 'הקישור הועתק',
                    text: 'הקישור הועתק בהצלחה ללוח',
                    duration: 2000
                });
            })
        }
    }
}
</script>

<style scoped>
.phoneSuggestions {
  position: absolute;
  top: 100%;
  right: 0;
  left: 0;
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  margin-top: 4px;
  max-height: 260px;
  overflow-y: auto;
  z-index: 20;
}
.phoneSuggestionsLine {
  position: sticky;
  top: 0;
  z-index: 1;
  background: white;
  border-bottom: 1px solid #f0f0f0;
  padding: 6px 12px;
  font-size: 12px;
  color: #999;
}
.phoneSuggestion {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
}
.phoneSuggestion:hover {
  background: #f6f7fb;
}
.suggestionName {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.inviteHint {
  margin-top: 6px;
  font-size: 13px;
  color: #38a169;
}
.inviteWordingHint {
  margin-top: 5px;
  font-size: 12px;
  color: #999;
}
.inviteWordingLink {
  color: #ab8ce4;
  cursor: pointer;
}
.inviteWordingLink:hover {
  text-decoration: underline;
}
.suggestionPhone {
  color: #999;
  font-size: 13px;
  direction: ltr;
  margin-right: 10px;
  flex: none;
}
</style>
